import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';

const TRAIT_LABELS = {
  cognitive_style_primary: {
    label: 'How you enter a story',
    image_first: 'Through images and atmosphere',
    structure_first: 'Through plot and structure',
    character_first: 'Through characters and people',
    theme_first: 'Through meaning and theme',
    feeling_first: 'Through tone and mood',
  },
  thinking_direction: {
    label: 'How you think',
    abstract_to_concrete: 'Ideas first, then specifics',
    concrete_to_abstract: 'Details first, then meaning',
    nonlinear: 'Freely between levels',
  },
  uncertainty_relationship: {
    label: 'How you experience open-endedness',
    energized: 'Uncertainty is exciting and generative',
    grounded: 'You need footing before exploring',
    constrained: 'Uncertainty needs structure to move past',
  },
  structural_preference: {
    label: 'Your relationship with frameworks',
    framework_driven: 'Frameworks feel orienting and useful',
    framework_resistant: 'Frameworks feel prescriptive — prefer discovery',
    framework_agnostic: 'Frameworks are tools you use selectively',
  },
};

export default function CreativeStylePanel() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async () => {
    try {
      const data = await api.get('/users/me/creative-style');
      setProfile(data);
    } catch (err) {
      console.error('Failed to load creative style:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProfile(); }, []);

  const handleToggle = async () => {
    try {
      const newVal = !profile?.cross_project_learning;
      await api.put('/users/me/creative-style', { cross_project_learning: newVal });
      fetchProfile();
    } catch (err) {
      console.error('Toggle failed:', err);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete all creative style data? This cannot be undone.')) return;
    try {
      await api.delete('/users/me/creative-style');
      fetchProfile();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  if (loading) return <div style={{ padding: 20, color: '#888' }}>Loading…</div>;

  const showTraits = profile && profile.sessions_observed >= 3;

  return (
    <div style={{ padding: '16px 20px' }}>
      <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 12px' }}>Creative Style</h3>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 16 }}>
        <input
          type="checkbox"
          checked={!!profile?.cross_project_learning}
          onChange={handleToggle}
        />
        <span style={{ fontSize: 14 }}>Remember my creative style across projects</span>
      </label>
      {!showTraits && (
        <p style={{ fontSize: 13, color: '#888780', lineHeight: 1.5 }}>
          {profile?.cross_project_learning
            ? `Brainstormy is learning your style. ${profile?.sessions_observed || 0} of 3 sessions observed so far.`
            : 'Turn on cross-project learning to build your creative style over time.'}
        </p>
      )}
      {showTraits && (
        <div style={{ fontSize: 13, lineHeight: 1.6 }}>
          {Object.entries(TRAIT_LABELS).map(([key, meta]) => {
            const value = profile?.[key];
            if (!value) return null;
            const description = meta[value] || value;
            return (
              <div key={key} style={{ marginBottom: 10 }}>
                <div style={{ color: '#5f5e5a', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{meta.label}</div>
                <div>{description}</div>
              </div>
            );
          })}
          <div style={{ color: '#888780', fontSize: 12, marginTop: 8 }}>
            Based on {profile.sessions_observed} session{profile.sessions_observed === 1 ? '' : 's'}
          </div>
        </div>
      )}
      {profile && profile.sessions_observed > 0 && (
        <button
          onClick={handleDelete}
          style={{ marginTop: 16, padding: '6px 14px', fontSize: 12, color: '#a02525', background: 'white', border: '1px solid #d3d1c7', borderRadius: 4, cursor: 'pointer' }}
        >
          Delete creative style data
        </button>
      )}
    </div>
  );
}
